/*
 * @ubm/ui/components/time-grid — UNIQUE peinture générique de la grille temps (déplacée depuis @ubm/booking-ui
 * styles.css : .ubm-bk-grid*, .ubm-bk-nowline*, cadre de .ubm-bk-slot). Valeurs par défaut IDENTIQUES à l'agenda
 * Booking ; chaque appelant peut les paramétrer par propriétés personnalisées (--tg-*) sur la racine. Token-driven
 * strict : aucun hex. Les règles métier (statuts, capacité, en-têtes de jour/lane, jauges) restent chez l'appelant.
 */

.grid {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--tg-border, var(--arc-border));
  border-radius: var(--tg-radius, var(--arc-radius-panel));
  background: var(--tg-surface, var(--arc-surface));
  overflow: hidden;
}

.head {
  display: grid;
  grid-template-columns: var(--tg-gutter, 54px) repeat(var(--tg-columns, 7), 1fr);
  border-bottom: 1px solid var(--tg-border, var(--arc-border));
}
.corner {
  border-right: 1px solid var(--tg-border, var(--arc-border));
}
/* Cellule d'en-tête — le filet vertical vit ici, plus sur le contenu. */
.colhdr {
  border-right: 1px solid var(--tg-border, var(--arc-border));
  min-width: 0;
}
.colhdr:last-child {
  border-right: none;
}

/* Rangée « journée entière » (extension WeekCalendar) — mêmes colonnes que l'en-tête. */
.allDay {
  display: grid;
  grid-template-columns: var(--tg-gutter, 54px) repeat(var(--tg-columns, 7), 1fr);
  border-bottom: 1px solid var(--tg-border, var(--arc-border));
}
.allDayLabel {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 7px;
  border-right: 1px solid var(--tg-border, var(--arc-border));
  font-size: var(--tg-label-size, var(--arc-text-xs));
  color: var(--tg-label-ink, var(--arc-text-muted));
}
.allDayCell {
  min-width: 0;
  padding: 2px;
  border-right: 1px solid var(--tg-border, var(--arc-border));
}
.allDayCell:last-child {
  border-right: none;
}
/* Cellule unique couvrant toutes les colonnes (barres multi-jours) : sa grille interne reprend les colonnes. */
.allDaySpan {
  grid-column: 2 / -1;
  display: grid;
  grid-template-columns: repeat(var(--tg-columns, 7), minmax(0, 1fr));
  grid-auto-rows: min-content;
  align-content: start;
  gap: 2px 0;
  min-width: 0;
  padding: 2px 0;
}

.body {
  display: grid;
  grid-template-columns: var(--tg-gutter, 54px) repeat(var(--tg-columns, 7), 1fr);
  flex: 1;
  /* 1 h = 52px (mockup Booking) — la hauteur porte le positionnement % des blocs. */
  min-height: calc(var(--tg-rows, 11) * var(--tg-hour-height, 52px));
}
.gutter {
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--tg-border, var(--arc-border));
}
.hourlab {
  flex: 1;
  text-align: right;
  padding-right: 7px;
  font-size: var(--tg-label-size, var(--arc-text-xs));
  color: var(--tg-label-ink, var(--arc-text-muted));
  transform: translateY(-6px);
}
.day {
  position: relative;
  border-right: 1px solid var(--tg-border, var(--arc-border));
  display: flex;
  flex-direction: column;
}
.day:last-child {
  border-right: none;
}
.hourline {
  flex: 1;
  border-bottom: 1px solid var(--tg-border, var(--arc-border));
}

/* Sélection glissée — fantôme token-driven. */
.select {
  position: absolute;
  left: 2px;
  right: 2px;
  background: var(--tg-select-fill, var(--arc-accent-subtle));
  border: 1px dashed var(--tg-select-ink, var(--arc-accent));
  border-radius: var(--tg-event-radius-sm, var(--arc-radius-control));
  pointer-events: none;
  z-index: 4;
}
/* Prévisualisation d'un évènement déplacé/étiré (extension) : même fantôme, trait plein. */
.preview {
  position: absolute;
  left: var(--tg-event-inset, 4px);
  right: var(--tg-event-inset, 4px);
  background: var(--tg-select-fill, var(--arc-accent-subtle));
  border: 1px solid var(--tg-select-ink, var(--arc-accent));
  border-radius: var(--tg-event-radius, var(--arc-radius-panel));
  pointer-events: none;
  z-index: 5;
}

/* Cadre positionné d'un évènement — teinte in-hue via --tg-event-accent (fallback action), contenu à l'appelant. */
.event {
  position: absolute;
  left: var(--tg-event-inset, 4px);
  right: var(--tg-event-inset, 4px);
  display: block;
  width: auto;
  padding: var(--tg-event-padding, 4px 7px);
  overflow: hidden;
  border: 1px solid color-mix(in oklch, var(--tg-event-accent, var(--arc-accent)) 22%, transparent);
  border-left: 3px solid var(--tg-event-accent, var(--arc-accent));
  border-radius: var(--tg-event-radius, var(--arc-radius-panel));
  background: color-mix(in oklch, var(--tg-event-accent, var(--arc-accent)) 13%, var(--tg-surface, var(--arc-surface)));
  font: inherit;
  font-size: var(--tg-event-size, var(--arc-text-xs));
  line-height: 1.25;
  text-align: left;
  color: var(--tg-event-ink, var(--arc-foreground));
  text-decoration: none;
  cursor: pointer;
  z-index: 2;
}
/* Poignée d'étirement (évènements déplaçables) : bande basse, curseur vertical. */
.handle {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 6px;
  cursor: ns-resize;
}

/* Ligne « maintenant » — encre chaude système (PAS le rouge d'alerte), SOUS les blocs (évènement z-index:2). */
.nowline {
  position: absolute;
  left: 0;
  right: 0;
  height: 0;
  border-top: 1px solid var(--tg-now-ink, var(--arc-danger));
  z-index: 1;
  pointer-events: none;
}
.nowline::before {
  content: "";
  position: absolute;
  left: -3.5px;
  top: -3.5px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--tg-now-ink, var(--arc-danger));
  box-shadow: 0 0 0 3px color-mix(in oklch, var(--tg-now-ink, var(--arc-danger)) 18%, transparent);
}
/* Pastille d'heure — posée dans la gouttière (colonne désignée uniquement). */
.nowlabel {
  position: absolute;
  left: -46px;
  top: -8px;
  width: 42px;
  text-align: right;
  font-size: 10px;
  font-weight: 700;
  color: var(--tg-now-ink, var(--arc-danger));
  background: var(--tg-surface, var(--arc-surface));
  padding: 0 3px;
  font-variant-numeric: tabular-nums;
}
